import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Navigate } from 'react-router-dom';
import { api, auditLogsAPI } from '../services/api';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  UserIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner, Button, Card, Input, Modal, BottomSheet } from '../components/ui';
import { PageHeader, DataTableCard } from '../components/ui';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';

interface AuditUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AuditLogRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  description: string;
  oldValues: unknown;
  newValues: unknown;
  metadata: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user: AuditUser | null;
}

const ACTION_LABELS: Record<string, string> = {
  CREATE_CLIENT: 'Alta cliente',
  IMPORT_CLIENTS: 'Importación masiva clientes',
  EXPORT_CLIENTS: 'Exportación masiva clientes',
  UPDATE_CLIENT: 'Edición cliente',
  DELETE_CLIENT: 'Baja cliente',
  CREATE_PAYMENT: 'Alta pago',
  UPDATE_PAYMENT: 'Edición pago',
  CANCEL_PAYMENT: 'Cancelación pago',
  DELETE_PAYMENT: 'Eliminación pago',
  CREATE_INVOICE: 'Alta factura',
  UPDATE_INVOICE: 'Edición factura',
  MARK_INVOICE_PAID: 'Factura pagada',
  MARK_INVOICE_OVERDUE: 'Factura vencida',
  GENERATE_INVOICE_MANUAL: 'Generación manual facturas',
  DELETE_INVOICE: 'Eliminación factura',
  BULK_MARK_OVERDUE_INVOICES: 'Marcado masivo vencidas',
  CREATE_PLAN_TEMPLATE: 'Alta plantilla plan',
  UPDATE_PLAN_TEMPLATE: 'Edición plantilla plan',
  DELETE_PLAN_TEMPLATE: 'Eliminación plantilla plan',
  CREATE_USER: 'Alta usuario',
  UPDATE_USER: 'Edición usuario',
  DELETE_USER: 'Eliminación usuario',
  CHANGE_USER_STATUS: 'Cambio estado usuario',
  RESET_USER_PASSWORD: 'Restablecer contraseña',
  UPDATE_PERMISSIONS: 'Actualización permisos (seed)',
  UPDATE_ROLE_PERMISSIONS: 'Matriz permisos por rol',
  UPDATE_CONFIGURATION: 'Configuración empresa',
  UPDATE_FISCAL_PROFILE: 'Perfil fiscal DGII',
  UPDATE_FISCAL_SEQUENCE: 'Secuencia e-NCF',
  UPDATE_FISCAL_BILLING: 'Facturación mensual fiscal',
  UPDATE_FISCAL_TAX: 'Perfil tributario',
  UPDATE_FISCAL_ENVIRONMENT: 'Ambiente DGII',
  UPLOAD_FISCAL_CERTIFICATE: 'Certificado fiscal',
  GENERATE_ENCF: 'Generación e-NCF',
  ECF_SIGN: 'Firma e-CF',
  ECF_SUBMIT: 'Envío DGII',
  ECF_ACCEPTED: 'e-CF aceptado',
  ECF_REJECTED: 'e-CF rechazado',
  UPDATE_NOTIFICATION_SETTINGS: 'Notificaciones programadas',
  UPDATE_EMAIL_SETTINGS: 'Correo / destinatarios',
  CRON_GENERATE_MONTHLY_INVOICES: 'Cron facturas mensuales',
  CRON_MARK_OVERDUE_INVOICES: 'Cron facturas vencidas',
  CRON_SEND_PAYMENT_REMINDERS: 'Cron recordatorios pago',
};

const ENTITY_LABELS: Record<string, string> = {
  CLIENT: 'Cliente',
  PAYMENT: 'Pago',
  INVOICE: 'Factura',
  PLAN_TEMPLATE: 'Plantilla de plan',
  USER: 'Usuario',
  PERMISSION: 'Permiso',
  ROLE_PERMISSION: 'Permisos por rol',
  CONFIGURATION: 'Configuración',
  EMAIL_NOTIFICATION: 'Correo / notificaciones',
  SYSTEM: 'Sistema',
};

const selectCls =
  'h-11 w-full rounded-xl border border-[#E5EAF1] bg-white px-3 text-sm font-medium text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200';

function formatJson(value: unknown): string {
  if (value === null || value === undefined) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('es-VE', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function actionTone(action: string): string {
  if (action.startsWith('DELETE') || action.includes('CANCEL')) {
    return 'bg-rose-50 text-rose-700 ring-rose-200';
  }
  if (action.startsWith('CREATE') || action.includes('PAID')) {
    return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  }
  if (action.startsWith('CRON') || action.includes('BULK')) {
    return 'bg-amber-50 text-amber-800 ring-amber-200';
  }
  return 'bg-primary-50 text-primary-800 ring-primary-200';
}

const AuditLog: React.FC = () => {
  const { isAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState<AuditLogRow | null>(null);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [isMobileUi, setIsMobileUi] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobileUi(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      search: search.trim() || undefined,
      userId: userId === '__system__' ? '__system__' : userId || undefined,
      entityType: entityType || undefined,
      action: action || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [page, search, userId, entityType, action, dateFrom, dateTo]
  );

  const { data: axiosRes, isLoading, isFetching, refetch } = useQuery(
    ['auditLogs', queryParams],
    () => auditLogsAPI.list(queryParams),
    { keepPreviousData: true }
  );

  const { data: usersRes } = useQuery('audit-users', () => api.get('/users'), {
    enabled: isAdmin,
  });

  const users: AuditUser[] = usersRes?.data?.data || [];
  const logs: AuditLogRow[] = axiosRes?.data?.data ?? [];
  const pagination = axiosRes?.data?.pagination;

  const activeFilterCount = [userId, entityType, action, dateFrom, dateTo].filter(Boolean).length;

  const clearFilters = () => {
    setSearch('');
    setUserId('');
    setEntityType('');
    setAction('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    toast.success('Filtros limpiados');
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 animate-fade-in-up pb-8 md:space-y-6">
      {/* Desktop header */}
      <div className="hidden md:block">
        <PageHeader
          title="Auditoría e historial"
          subtitle="Trazabilidad de acciones críticas del sistema (solo administradores)."
        />
      </div>

      {/* Mobile header */}
      <div className="flex items-start justify-between gap-3 md:hidden">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Auditoría</h1>
          <p className="mt-1 text-sm text-slate-500">Historial de acciones del sistema.</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5EAF1] bg-white text-slate-600 transition active:scale-95"
          aria-label="Actualizar"
        >
          <ArrowPathIcon className={clsx('h-5 w-5', isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Mobile search + filter */}
      <div className="space-y-3 md:hidden">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar en descripción, acción…"
              className="h-11 w-full rounded-2xl border border-[#E5EAF1] bg-white pl-10 pr-10 text-sm font-medium text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            {search ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 hover:bg-slate-100"
                aria-label="Limpiar búsqueda"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E5EAF1] bg-primary-50 text-primary-700 transition active:scale-95"
            aria-label="Abrir filtros"
          >
            <FunnelIcon className="h-5 w-5" />
            {activeFilterCount > 0 ? (
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary-600 ring-2 ring-primary-50" />
            ) : null}
          </button>
        </div>

        <div className="flex items-center justify-between border-b border-[#E5EAF1] pb-2">
          <p className="text-sm font-medium text-slate-600">
            <span className="tabular-nums font-semibold text-slate-900">
              {pagination?.total ?? logs.length}
            </span>{' '}
            eventos
          </p>
          {activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-semibold text-rose-600"
            >
              Limpiar filtros
            </button>
          ) : null}
        </div>

        {isLoading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[112px] animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-14 text-center">
            <DocumentMagnifyingGlassIcon className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-800">Sin eventos</p>
            <p className="mt-1 text-xs text-slate-500">
              No hay registros con los filtros seleccionados.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {logs.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setDetail(row)}
                className="w-full rounded-2xl border border-[#E5EAF1] bg-white p-3.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-150 active:scale-[0.99] active:bg-slate-50/80"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                    {row.user ? (
                      <UserIcon className="h-5 w-5" />
                    ) : (
                      <ClockIcon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {row.user?.name || 'Sistema'}
                      </p>
                      <span className="shrink-0 text-[11px] font-medium text-slate-400">
                        {formatWhen(row.createdAt)}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <span
                        className={clsx(
                          'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1',
                          actionTone(row.action)
                        )}
                      >
                        {ACTION_LABELS[row.action] || row.action}
                      </span>
                      <span className="inline-flex rounded-md bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">
                        {ENTITY_LABELS[row.entityType] || row.entityType}
                        {row.entityId ? ` · ${row.entityId}` : ''}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-slate-600">
                      {row.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop filters */}
      <Card className="hidden border border-slate-200/80 p-5 shadow-sm md:block">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">Búsqueda</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Texto en descripción, acción, entidad o ID…"
                className="pl-9"
              />
            </div>
          </div>
          <div className="w-44">
            <label className="mb-1 block text-xs font-medium text-slate-600">Usuario</label>
            <select
              aria-label="Filtrar por usuario"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="__system__">Sistema (automático)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-44">
            <label className="mb-1 block text-xs font-medium text-slate-600">Entidad</label>
            <select
              aria-label="Filtrar por tipo de entidad"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="w-52">
            <label className="mb-1 block text-xs font-medium text-slate-600">Acción</label>
            <select
              aria-label="Filtrar por acción"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="w-36">
            <label className="mb-1 block text-xs font-medium text-slate-600">Desde</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-36">
            <label className="mb-1 block text-xs font-medium text-slate-600">Hasta</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setPage(1);
              refetch();
            }}
            className="shrink-0"
          >
            <FunnelIcon className="mr-1.5 h-4 w-4" />
            Aplicar
          </Button>
          <Button variant="ghost" onClick={clearFilters}>
            Limpiar
          </Button>
          <Button variant="ghost" onClick={() => refetch()} title="Actualizar">
            <ArrowPathIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </Card>

      {/* Desktop table */}
      <div className="hidden md:block">
        <DataTableCard>
          <div className="flex flex-col gap-1 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Registros</h2>
              {pagination && (
                <p className="text-sm text-slate-500">
                  Página {pagination.page} de {pagination.totalPages} · {pagination.total} eventos
                </p>
              )}
            </div>
          </div>
          {isLoading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              No hay eventos con los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Acción
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Entidad
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Descripción
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Detalle
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {logs.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {formatWhen(row.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        {row.user ? (
                          <div>
                            <p className="font-medium text-slate-900">{row.user.name}</p>
                            <p className="text-xs text-slate-500">{row.user.email}</p>
                          </div>
                        ) : (
                          <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                            Sistema
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800 ring-1 ring-inset ring-indigo-600/15">
                          {ACTION_LABELS[row.action] || row.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-800">
                          {ENTITY_LABELS[row.entityType] || row.entityType}
                        </span>
                        {row.entityId && (
                          <span className="mt-0.5 block text-xs text-slate-500">
                            ID {row.entityId}
                          </span>
                        )}
                      </td>
                      <td className="max-w-md px-4 py-3 text-slate-700">
                        <span className="line-clamp-2">{row.description}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDetail(row)}
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
                        >
                          <DocumentMagnifyingGlassIcon className="mr-1 h-4 w-4" />
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DataTableCard>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between rounded-2xl border border-[#E5EAF1] bg-white px-4 py-3 md:rounded-none md:border-0 md:bg-transparent md:px-1">
          <p className="text-xs text-slate-500">
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <AuditFiltersSheet
        open={filtersSheetOpen}
        onClose={() => setFiltersSheetOpen(false)}
        users={users}
        userId={userId}
        entityType={entityType}
        action={action}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onApply={(next) => {
          setUserId(next.userId);
          setEntityType(next.entityType);
          setAction(next.action);
          setDateFrom(next.dateFrom);
          setDateTo(next.dateTo);
          setPage(1);
        }}
        onClear={() => {
          setUserId('');
          setEntityType('');
          setAction('');
          setDateFrom('');
          setDateTo('');
          setPage(1);
        }}
      />

      {/* Mobile detail sheet */}
      {isMobileUi ? (
        <BottomSheet open={!!detail} onClose={() => setDetail(null)} title="Detalle del evento">
          {detail ? <AuditDetailBody detail={detail} /> : null}
        </BottomSheet>
      ) : (
        <Modal
          isOpen={!!detail}
          onClose={() => setDetail(null)}
          title="Detalle del evento"
          size="lg"
        >
          {detail ? <AuditDetailBody detail={detail} /> : null}
        </Modal>
      )}
    </div>
  );
};

const AuditDetailBody: React.FC<{ detail: AuditLogRow }> = ({ detail }) => (
  <div className="space-y-4 text-sm">
    <div className="grid grid-cols-1 gap-3 text-slate-700 sm:grid-cols-2">
      <div>
        <span className="text-xs font-semibold uppercase text-slate-500">Acción</span>
        <p className="mt-0.5 font-medium">
          {ACTION_LABELS[detail.action] || detail.action}
        </p>
      </div>
      <div>
        <span className="text-xs font-semibold uppercase text-slate-500">Entidad</span>
        <p className="mt-0.5">
          {ENTITY_LABELS[detail.entityType] || detail.entityType}
          {detail.entityId ? ` · ID ${detail.entityId}` : ''}
        </p>
      </div>
      <div>
        <span className="text-xs font-semibold uppercase text-slate-500">Fecha</span>
        <p className="mt-0.5">{new Date(detail.createdAt).toLocaleString('es-VE')}</p>
      </div>
      <div>
        <span className="text-xs font-semibold uppercase text-slate-500">Actor</span>
        <p className="mt-0.5">{detail.user?.name || 'Sistema'}</p>
      </div>
      <div>
        <span className="text-xs font-semibold uppercase text-slate-500">IP</span>
        <p className="mt-0.5">{detail.ipAddress || '—'}</p>
      </div>
      <div>
        <span className="text-xs font-semibold uppercase text-slate-500">User-Agent</span>
        <p className="mt-0.5 break-all text-xs">{detail.userAgent || '—'}</p>
      </div>
    </div>
    <div>
      <span className="text-xs font-semibold uppercase text-slate-500">Descripción</span>
      <p className="mt-1 text-slate-800">{detail.description}</p>
    </div>
    <div>
      <span className="text-xs font-semibold uppercase text-slate-500">Valores anteriores</span>
      <pre className="mt-1 max-h-48 overflow-auto rounded-xl bg-slate-900/90 p-3 font-mono text-xs leading-relaxed text-emerald-100/95">
        {formatJson(detail.oldValues)}
      </pre>
    </div>
    <div>
      <span className="text-xs font-semibold uppercase text-slate-500">Valores nuevos</span>
      <pre className="mt-1 max-h-48 overflow-auto rounded-xl bg-slate-900/90 p-3 font-mono text-xs leading-relaxed text-sky-100/95">
        {formatJson(detail.newValues)}
      </pre>
    </div>
    <div>
      <span className="text-xs font-semibold uppercase text-slate-500">Metadatos</span>
      <pre className="mt-1 max-h-40 overflow-auto rounded-xl bg-slate-100 p-3 font-mono text-xs text-slate-800">
        {formatJson(detail.metadata)}
      </pre>
    </div>
  </div>
);

const AuditFiltersSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  users: AuditUser[];
  userId: string;
  entityType: string;
  action: string;
  dateFrom: string;
  dateTo: string;
  onApply: (next: {
    userId: string;
    entityType: string;
    action: string;
    dateFrom: string;
    dateTo: string;
  }) => void;
  onClear: () => void;
}> = ({
  open,
  onClose,
  users,
  userId,
  entityType,
  action,
  dateFrom,
  dateTo,
  onApply,
  onClear,
}) => {
  const [draft, setDraft] = useState({ userId, entityType, action, dateFrom, dateTo });

  useEffect(() => {
    if (open) {
      setDraft({ userId, entityType, action, dateFrom, dateTo });
    }
  }, [open, userId, entityType, action, dateFrom, dateTo]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Filtros"
      footer={
        <div className="space-y-2 pb-1">
          <button
            type="button"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
            className="flex h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 active:scale-[0.99]"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft({
                userId: '',
                entityType: '',
                action: '',
                dateFrom: '',
                dateTo: '',
              });
              onClear();
              onClose();
            }}
            className="flex h-10 w-full items-center justify-center text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            Limpiar filtros
          </button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Usuario</span>
          <select
            className={selectCls}
            value={draft.userId}
            onChange={(e) => setDraft((d) => ({ ...d, userId: e.target.value }))}
          >
            <option value="">Todos</option>
            <option value="__system__">Sistema (automático)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Entidad</span>
          <select
            className={selectCls}
            value={draft.entityType}
            onChange={(e) => setDraft((d) => ({ ...d, entityType: e.target.value }))}
          >
            <option value="">Todas</option>
            {Object.entries(ENTITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Acción</span>
          <select
            className={selectCls}
            value={draft.action}
            onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
          >
            <option value="">Todas</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-600">Desde</span>
            <input
              type="date"
              className={selectCls}
              value={draft.dateFrom}
              onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-600">Hasta</span>
            <input
              type="date"
              className={selectCls}
              value={draft.dateTo}
              onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
            />
          </label>
        </div>
      </div>
    </BottomSheet>
  );
};

export default AuditLog;
